// ─────────────────────────────────────────────────────────────────────────────
// CSV PARSING – TopStep + common futures broker exports
//
// Handles two TopStep formats observed in real exports:
//
//   FORMAT A (older / our test data):
//     EnteredAt: 2026-02-13T14:22:01
//     TradeDay:  2026-02-13
//
//   FORMAT B (newer real TopStep export):
//     EnteredAt: 05/10/2026 20:17:17 -04:00
//     TradeDay:  05/11/2026 00:00:00 -05:00
//
// trade_date is taken from TradeDay when present, falling back to
// the date portion of EnteredAt, then local-today as last resort.
// All dates are stored as YYYY-MM-DD strings.
//
// exited_at is stored as a full ISO timestamp (with timezone preserved)
// so the trade journal can sort by exact close time matching the broker.
// ─────────────────────────────────────────────────────────────────────────────

function localTodayYMD() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

// Converts a parsed ISO timestamp to HH:MM:SS in America/New_York.
// Used by parseDateTimeString whenever the source string carried an
// explicit timezone (Z or ±HH:MM) — in that case the raw digits in the
// string are NOT reliably ET wall-clock time (e.g. Tradovate's "_timestamp"
// column is UTC with a "Z" suffix), so we must convert via the ISO value
// instead of trusting the digits verbatim. isSessionShifted(), the 🌙
// session-shift icon, and the journal display all assume entry_time/
// exit_time are already ET — this is what keeps that assumption true.
function isoToEasternTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(d);
  const get = (type) => parts.find(p => p.type === type)?.value;
  let hh = get("hour");
  if (hh === "24") hh = "00"; // Intl 24:00-for-midnight quirk
  return `${hh}:${get("minute")}:${get("second")}`;
}

// Parse various date/datetime formats into { date, time, iso }.
// iso = full ISO 8601 string for sorting (preserves timezone).
// time = HH:MM:SS, always ET wall-clock (see isoToEasternTime above).
function parseDateTimeString(raw) {
  if (!raw || typeof raw !== "string") return { date: null, time: null, iso: null };
  const trimmed = raw.trim();
  if (!trimmed) return { date: null, time: null, iso: null };

  // Extract timezone suffix before stripping it
  const tzMatch = trimmed.match(/([+-]\d{2}:?\d{2}|Z)\s*$/);
  const tzSuffix = tzMatch ? tzMatch[1] : null;

  const noTz = trimmed.replace(/\s*[+-]\d{2}:?\d{2}\s*$/, "").replace(/\s*Z\s*$/, "").trim();

  let datePart, timePart;
  if (noTz.includes("T")) {
    [datePart, timePart] = noTz.split("T");
  } else if (noTz.includes(" ")) {
    [datePart, timePart] = noTz.split(/\s+/);
  } else {
    datePart = noTz;
    timePart = null;
  }

  // Normalise date to YYYY-MM-DD
  let normalisedDate = null;
  if (datePart) {
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      normalisedDate = datePart;
    } else if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(datePart)) {
      const [m, d, y] = datePart.split("/");
      normalisedDate = `${y}-${m.padStart(2,"0")}-${d.padStart(2,"0")}`;
    }
  }

  // Normalise time to HH:MM:SS (dropping subseconds for display).
  // NOTE: these are the RAW digits from the source string — only valid
  // as ET wall-clock if the source had no timezone suffix (i.e. it was
  // already local). When a tzSuffix is present, this gets overwritten
  // below with the properly converted ET time.
  let normalisedTime = null;
  if (timePart) {
    const m = timePart.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (m) {
      const hh = m[1].padStart(2, "0");
      const mm = m[2];
      const ss = m[3] || "00";
      normalisedTime = `${hh}:${mm}:${ss}`;
    }
  }

  // Build full ISO timestamp for precise sorting
  // Preserves subseconds and original timezone offset
  let isoTimestamp = null;
  if (normalisedDate && timePart) {
    // Keep the full time including subseconds from original
    const fullTime = timePart.match(/^(\d{1,2}:\d{2}:\d{2}(?:\.\d+)?)/)?.[1] || normalisedTime;
    if (fullTime) {
      try {
        isoTimestamp = new Date(`${normalisedDate}T${fullTime}${tzSuffix || ""}`).toISOString();
      } catch {
        isoTimestamp = null;
      }
    }
  }

  // If the source string carried an explicit timezone, don't trust the
  // raw digits as ET — recompute `time` from the (correctly timezone-
  // aware) ISO timestamp. Sources with NO timezone suffix (TopStep
  // FORMAT A, TradingView, etc.) are assumed already local/ET and are
  // left as-is.
  if (isoTimestamp && tzSuffix) {
    const etTime = isoToEasternTime(isoTimestamp);
    if (etTime) normalisedTime = etTime;
  }

  return { date: normalisedDate, time: normalisedTime, iso: isoTimestamp };
}

// Handles every known broker P&L format:
//   "$12.00"      →  12.00
//   "-$5.25"      →  -5.25
//   "($2.00)"     →  -2.00   (Tradovate accounting notation)
//   "$(12.00)"    →  -12.00  (alternate accounting notation)
//   "(2.00)"      →  -2.00
//   "$1,234.50"   →  1234.50
//   null / ""     →  0
function parsePnl(raw) {
  if (raw == null) return 0;
  const s = String(raw).trim();
  if (!s || s === "N/A" || s === "-") return 0;
  const isNegative = (/^\(/.test(s) || /^\$\(/.test(s)) && /\)$/.test(s);
  const cleaned = s
    .replace(/^\$\(/, "")
    .replace(/^\(/, "")
    .replace(/\)$/, "")
    .replace(/[$,\s]/g, "");
  const n = parseFloat(cleaned);
  if (isNaN(n)) return 0;
  return isNegative ? -Math.abs(n) : n;
}

export function parseCSV(text) {
  // Strip UTF-8 BOM if present
  if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);

  // Sierra Chart exports tab-delimited text. Detect before CSV splitting.
  const firstLine = text.slice(0, (text.indexOf("\n") + 1) || 500);
  if (firstLine.includes("\t") && !firstLine.includes(",")) {
    return parseSierraChart(text);
  }

  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];
  const headers = lines[0]
    .split(",")
    .map(h => h.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_"));

  // TradingView paper trading export: detected by "fill_price" + "placing_time" + "closing_time"
  if (headers.includes("fill_price") && headers.includes("placing_time") && headers.includes("closing_time")) {
    return parseTradingView(headers, lines.slice(1));
  }

  // Tradovate Performance format is pre-paired (one row = one round-trip
  // trade) with buyPrice/sellPrice/boughtTimestamp/soldTimestamp/pnl.
  // We detect it by the presence of buyprice + sellprice (after header
  // normalization) and route to a dedicated handler — the field shape is
  // different enough that it's cleaner than overloading the generic path.
  if (headers.includes("buyprice") && headers.includes("sellprice")) {
    return parseTradovatePerformance(headers, lines.slice(1));
  }

  // Tradovate Fills export: fill-level rows with fill_id + commission +
  // _timestamp. Preferred over Orders export because it includes commission
  // data and millisecond timestamps. Detected by fill_id + commission presence.
  // Must be checked BEFORE the Orders detection since both have b_s.
  if (headers.includes("fill_id") && headers.includes("commission") && headers.includes("_timestamp")) {
    return parseTradovateFills(headers, lines.slice(1));
  }

  // Tradovate Orders export: fill-level rows with b_s + avgprice + filledqty
  // but NO pnl column. Detected by the combination of b_s + avgprice +
  // filledqty. Must be checked BEFORE the generic path since those columns
  // also partially match the generic aliases, producing $0 trades otherwise.
  if (headers.includes("b_s") && headers.includes("avgprice") && headers.includes("filledqty")) {
    return parseTradovateOrders(headers, lines.slice(1));
  }

  // NinjaTrader Executions format: detected by presence of "action" + "e_x"
  // (the Entry/Exit flag column). Fill-level rows paired via E/X flag.
  if (headers.includes("action") && headers.includes("e_x")) {
    return parseNinjaTraderExecutions(headers, lines.slice(1));
  }

  // Aliases include the broker-export names (EnteredAt, ContractName, …)
  // AND the normalized forms of our own export-side headers, so a user can
  // re-import a CSV they exported from FuturesOS. The header normalizer
  // strips special chars and lowercases, so "P&L" becomes "p_l", "Net P&L"
  // becomes "net_p_l", "R:R" becomes "r_r", etc. Those go in here so the
  // round-trip works.
  //
  // Tradovate Orders export (different schema than TopStep): uses "Contract"
  // not "ContractName", "B/S" for direction ("b_s" after normalization),
  // "filledQty" / "Filled Qty" / "Quantity" for size, "avgPrice" / "Avg Fill
  // Price" for entry, "Timestamp" / "Fill Time" / "Date" for date/time.
  // We add all these so Tradovate imports work without the user having to
  // rename headers manually.
  const map = {
    id:           ["id", "broker_trade_id", "orderid", "order_id", "order_number"],
    contract:     ["contractname", "symbol", "instrument", "ticker", "contract", "product"],
    entered_at:   ["enteredat", "entry_time", "time", "timestamp", "fill_time", "update_time"],
    exited_at:    ["exitedat", "exit_time"],
    entry_price:  ["entryprice", "entry_price", "open_price", "fill_price", "entry",
                   "avgprice", "avg_fill_price"],
    exit_price:   ["exitprice", "exit_price", "close_price", "exit"],
    fees:         ["fees"],
    commissions:  ["commissions", "commission"],
    pnl:          ["pnl", "profit_loss", "net_pnl", "p_l", "net_p_l"],
    size:         ["size", "quantity", "qty", "contracts", "filledqty", "filled_qty", "qty_filled"],
    type:         ["type", "direction", "b_s", "side", "buy_sell"],
    trade_day:    ["tradeday", "trade_date", "date"],
    duration:     ["tradeduration", "duration"],
  };

  const colIdx = {};
  Object.entries(map).forEach(([key, aliases]) => {
    colIdx[key] = headers.findIndex(h => aliases.includes(h));
  });

  return lines.slice(1).filter(l => l.trim()).map((line) => {
    const cols = line.split(",").map(c => c.trim().replace(/^"|"$/g, ""));
    const get  = (key) => (colIdx[key] !== -1 ? cols[colIdx[key]] : null);

    const entered  = parseDateTimeString(get("entered_at") || "");
    const exited   = parseDateTimeString(get("exited_at")  || "");
    const tradeDay = parseDateTimeString(get("trade_day")  || "");

    // Direction parsing: handle long-form ("buy"/"sell"/"long"/"short"),
    // Tradovate's single-letter ("B"/"S"), and fall through to "" otherwise.
    const rawType = (get("type") || "").toLowerCase().trim();
    const direction = rawType === "b"           ? "Long"
                    : rawType === "s"           ? "Short"
                    : rawType.includes("buy")   ? "Long"
                    : rawType.includes("sell")  ? "Short"
                    : rawType.includes("long")  ? "Long"
                    : rawType.includes("short") ? "Short" : "";

    const parseDuration = (raw) => {
      if (!raw) return null;
      const clean = raw.split(".")[0];
      if (clean.includes(":")) {
        const parts = clean.split(":").map(Number);
        if (parts.length === 3) return parts[0] * 60 + parts[1];
        if (parts.length === 2) return parts[0];
      }
      const secs = parseInt(clean);
      return isNaN(secs) ? null : Math.round(secs / 60);
    };

    const pnl = parsePnl(get("pnl"));
    const tradeDate = tradeDay.date || entered.date || localTodayYMD();

    return {
      symbol:           (get("contract") || "Unknown").replace(/\s+/g, ""),
      direction,
      entry_time:       entered.time || "",
      exit_time:        exited.time  || "",
      exited_at:        exited.iso   || null,   // ← full ISO timestamp for broker-accurate sort
      trade_date:       tradeDate,
      entry_price:      parseFloat(get("entry_price")) || 0,
      exit_price:       parseFloat(get("exit_price"))  || 0,
      position_size:    parseFloat(get("size"))         || 1,
      fees:             parseFloat(get("fees"))          || 0,
      commissions:      parseFloat(get("commissions"))   || 0,
      pnl,
      result:           pnl > 0 ? "Win" : pnl < 0 ? "Loss" : "Breakeven",
      duration_minutes: parseDuration(get("duration")),
      broker_trade_id:  get("id") ? String(get("id")) : null,
      import_source:    "csv",
      created_at:       new Date().toISOString(),
    };
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// TRADOVATE FILLS FORMAT
//
// Header signature (after normalization):
//   fill_id, order_id, _timestamp, _tradedate, b_s, quantity, price,
//   contract, product, commission
//
// This is the preferred Tradovate export — includes commission per fill and
// millisecond timestamps. One row per individual fill (partial fills each
// get their own row).
//
// Trade reconstruction: group fills by net position crossing zero.
// All fills between flat→open→flat = one trade.
//   - Entry price  = VWAP of all entry-direction fills
//   - Exit price   = VWAP of all exit-direction fills
//   - P&L (gross)  = (sell_vwap - buy_vwap) × qty × pointValue
//   - P&L (net)    = gross − sum(commission for all fills in group)
//   - Direction    = Long if first fill is Buy, Short if first fill is Sell
//
// When position overshoots zero (simultaneous close+open), the excess qty
// seeds the next trade at the closing fill's price.
// ─────────────────────────────────────────────────────────────────────────────
function parseTradovateFills(headers, dataLines) {
  const idx = (name) => headers.indexOf(name);
  const col = {
    fillId:    idx("fill_id"),
    timestamp: idx("_timestamp"),
    tradeDate: idx("_tradedate") !== -1 ? idx("_tradedate") : idx("date"),
    bs:        idx("b_s"),
    quantity:  idx("quantity"),
    price:     idx("price"),
    contract:  idx("contract"),
    product:   idx("product"),
    commission:idx("commission"),
  };

  // Parse all rows into fill objects, sorted by timestamp
  const fills = dataLines
    .filter(l => l.trim())
    .map(line => {
      const cols = line.split(",").map(c => c.trim().replace(/^"|"$/g, ""));
      const get  = (key) => (col[key] !== -1 ? cols[col[key]] : null);
      const side = (get("bs") || "").trim();
      if (side !== "Buy" && side !== "Sell") return null;
      const price = parseFloat(get("price"));
      const qty   = parseFloat(get("quantity"));
      if (isNaN(price) || isNaN(qty) || qty <= 0) return null;
      const dt       = parseDateTimeString((get("timestamp") || "").trim());
      const dateOnly = parseDateTimeString((get("tradeDate") || "").trim());
      const contract = (get("contract") || "").trim();
      const product  = productRoot(contract) || (get("product") || "").trim().toUpperCase();
      return {
        side,
        price,
        qty,
        contract,
        product,
        fillTime:   dt,
        tradeDate:  dateOnly.date || dt.date,
        commission: parseFloat(get("commission")) || 0,
        fillId:     get("fillId"),
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (a.fillTime.iso && b.fillTime.iso) return a.fillTime.iso.localeCompare(b.fillTime.iso);
      return 0;
    });

  // Group fills into trades by net position crossing zero.
  // A fill that overshoots zero is SPLIT: the portion that closes the current
  // position stays with the closing group, the remainder opens the next one.
  // Commission is prorated across the split — never counted in both groups.
  let netPos = 0;
  let groupFills = [];
  const tradeGroups = [];

  for (const fill of fills) {
    const signedQty = fill.side === "Buy" ? fill.qty : -fill.qty;
    const commPerContract = fill.qty ? fill.commission / fill.qty : 0;
    let remaining = signedQty;

    while (remaining !== 0) {
      let take;
      if (netPos === 0 || Math.sign(netPos) === Math.sign(remaining)) {
        take = remaining;                              // opening or adding
      } else if (Math.abs(remaining) <= Math.abs(netPos)) {
        take = remaining;                              // partial or exact close
      } else {
        take = -netPos;                                // close only what's open
      }

      const absTake = Math.abs(take);
      groupFills.push({ ...fill, qty: absTake, commission: commPerContract * absTake });

      netPos    += take;
      remaining -= take;

      if (netPos === 0) {
        tradeGroups.push(groupFills);
        groupFills = [];
      }
    }
  }

  // Convert each group into a trade record
  return tradeGroups.map(group => {
    const contract  = group[0].contract;
    const product   = group[0].product;
    const pv        = TRADOVATE_POINT_VALUES[product] ?? 20;
    const direction = group[0].side === "Sell" ? "Short" : "Long";

    const buys  = group.filter(f => f.side === "Buy");
    const sells = group.filter(f => f.side === "Sell");
    const buyQty   = buys.reduce((s, f) => s + f.qty, 0);
    const sellQty  = sells.reduce((s, f) => s + f.qty, 0);
    const buyVwap  = buyQty  > 0 ? buys.reduce((s, f)  => s + f.price * f.qty, 0) / buyQty  : 0;
    const sellVwap = sellQty > 0 ? sells.reduce((s, f) => s + f.price * f.qty, 0) / sellQty : 0;
    const tradeQty = Math.min(buyQty, sellQty);
    const totalComm = group.reduce((s, f) => s + f.commission, 0);

    const grossPnl = Math.round(((sellVwap - buyVwap) * tradeQty * pv) * 100) / 100;
    const netPnl   = Math.round((grossPnl - totalComm) * 100) / 100;

    const entryFill = group[0];
    const exitFill  = group[group.length - 1];
    const entryTime = entryFill.fillTime;
    const exitTime  = exitFill.fillTime;

    const durationMs  = (entryTime.iso && exitTime.iso)
      ? new Date(exitTime.iso).getTime() - new Date(entryTime.iso).getTime()
      : null;
    const durationMin = durationMs !== null ? Math.round(durationMs / 60000) : null;

    const entryPrice = direction === "Long" ? buyVwap  : sellVwap;
    const exitPrice  = direction === "Long" ? sellVwap : buyVwap;

    return {
      symbol:           contract.replace(/\s+/g, "") || product,
      direction,
      entry_price:      Math.round(entryPrice * 100) / 100,
      exit_price:       Math.round(exitPrice  * 100) / 100,
      position_size:    tradeQty,
      pnl:              grossPnl,
      result:           netPnl > 0 ? "Win" : netPnl < 0 ? "Loss" : "Breakeven",
      trade_date:       entryFill.tradeDate || entryTime.date || localTodayYMD(),
      entry_time:       entryTime.time || "",
      exit_time:        exitTime.time  || "",
      exited_at:        exitTime.iso   || null,
      duration_minutes: durationMin,
      fees:             0,
      commissions:      Math.round(totalComm * 100) / 100,
      broker_trade_id:  entryFill.fillId ? String(entryFill.fillId) : null,
      import_source:    "csv",
      created_at:       new Date().toISOString(),
    };
  });
}

// TRADOVATE ORDERS FORMAT
//
// Header signature (after normalization):
//   orderid, account, b_s, contract, product, avgprice, filledqty,
//   fill_time, status, _ticksize
//
// This is a fill-level export — one row per order fill, no P&L column.
// We reconstruct round-trip trades using FIFO position tracking:
//   - Fills in the same direction accumulate as open lots
//   - An opposite-direction fill closes lots oldest-first (FIFO)
//   - P&L = (exitPrice - entryPrice) × qty × pointValue  (Long)
//          (entryPrice - exitPrice) × qty × pointValue  (Short)
//
// Point values per product root:
//   NQ  = $20/pt   MNQ = $2/pt
//   ES  = $50/pt   MES = $5/pt
//   YM  = $5/pt    MYM = $0.50/pt
//   RTY = $50/pt   M2K = $5/pt
//   GC  = $100/pt  SI  = $5000/pt  CL = $1000/pt
//
// Only "Filled" status rows are processed; Canceled/Rejected are ignored.
// ─────────────────────────────────────────────────────────────────────────────
const TRADOVATE_POINT_VALUES = {
  NQ:  20,   MNQ: 2,
  ES:  50,   MES: 5,
  YM:  5,    MYM: 0.5,
  RTY: 50,   M2K: 5,
  GC:  100,  SI:  5000, CL: 1000,
};

function productRoot(contract) {
  // Strip expiry suffix: "NQU6" → "NQ", "ESZ25" → "ES", "MNQH6" → "MNQ"
  return (contract || "").replace(/[A-Z]\d{1,2}$/, "").toUpperCase();
}

function parseTradovateOrders(headers, dataLines) {
  const idx = (name) => headers.indexOf(name);
  const col = {
    orderId:   idx("orderid"),
    account:   idx("account"),
    bs:        idx("b_s"),
    contract:  idx("contract"),
    product:   idx("product"),
    avgPrice:  idx("avgprice"),
    filledQty: idx("filledqty"),
    fillTime:  idx("fill_time"),
    status:    idx("status"),
  };

  // Parse all filled rows into fill objects sorted by fill time
  const fills = dataLines
    .filter(l => l.trim())
    .map(line => {
      const cols = line.split(",").map(c => c.trim().replace(/^"|"$/g, ""));
      const get  = (key) => (col[key] !== -1 ? cols[col[key]] : null);
      const status = (get("status") || "").trim().toLowerCase();
      if (status !== "filled") return null;
      const side = (get("bs") || "").trim().toLowerCase();
      if (side !== "buy" && side !== "sell") return null;
      const price = parseFloat(get("avgPrice"));
      const qty   = parseFloat(get("filledQty"));
      if (isNaN(price) || isNaN(qty) || qty <= 0) return null;
      const dt = parseDateTimeString((get("fillTime") || "").trim());
      const contract = (get("contract") || get("product") || "").trim();
      return {
        side,       // "buy" | "sell"
        price,
        qty,
        contract,
        product:    productRoot(contract) || (get("product") || "").trim().toUpperCase(),
        fillTime:   dt,
        orderId:    get("orderId"),
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      // Sort by fill time ISO string; fall back to original order
      if (a.fillTime.iso && b.fillTime.iso) return a.fillTime.iso.localeCompare(b.fillTime.iso);
      return 0;
    });

  // FIFO position tracker → closed trades
  const position = []; // open lots: { price, qty, side, fillTime, orderId, product }
  const trades   = [];

  for (const fill of fills) {
    const pv = TRADOVATE_POINT_VALUES[fill.product] ?? 20; // default NQ value

    if (position.length === 0 || position[0].side === fill.side) {
      // Same direction — add to open position
      position.push({ ...fill });
    } else {
      // Opposite direction — close lots FIFO
      let remainingClose = fill.qty;

      while (remainingClose > 0 && position.length > 0) {
        const lot     = position[0];
        const matched = Math.min(lot.qty, remainingClose);

        const isShort = lot.side === "sell"; // entry was a sell → short trade
        const pnl = isShort
          ? (lot.price - fill.price) * matched * pv
          : (fill.price - lot.price) * matched * pv;

        const direction   = isShort ? "Short" : "Long";
        const entryFill   = lot.fillTime;
        const exitFill    = fill.fillTime;
        const entryPrice  = lot.price;
        const exitPrice   = fill.price;

        const durationMs  = (entryFill.iso && exitFill.iso)
          ? new Date(exitFill.iso).getTime() - new Date(entryFill.iso).getTime()
          : null;
        const durationMin = durationMs !== null ? Math.round(durationMs / 60000) : null;

        trades.push({
          symbol:           fill.contract.replace(/\s+/g, "") || fill.product,
          direction,
          entry_price:      entryPrice,
          exit_price:       exitPrice,
          position_size:    matched,
          pnl:              Math.round(pnl * 100) / 100,
          result:           pnl > 0 ? "Win" : pnl < 0 ? "Loss" : "Breakeven",
          trade_date:       entryFill.date || localTodayYMD(),
          entry_time:       entryFill.time || "",
          exit_time:        exitFill.time  || "",
          exited_at:        exitFill.iso   || null,
          duration_minutes: durationMin,
          fees:             0,
          commissions:      0,   // not in Orders export — add manually or via Cash History
          broker_trade_id:  lot.orderId ? String(lot.orderId) : null,
          import_source:    "csv",
          created_at:       new Date().toISOString(),
        });

        lot.qty      -= matched;
        remainingClose -= matched;
        if (lot.qty === 0) position.shift();
      }

      // Any remaining qty opens a new position in the opposite direction
      if (remainingClose > 0) {
        position.push({ ...fill, qty: remainingClose });
      }
    }
  }

  return trades;
}

// TRADOVATE PERFORMANCE FORMAT
//
// Header signature:
//   symbol, _priceFormat, _priceFormatType, _tickSize,
//   buyFillId, sellFillId, qty, buyPrice, sellPrice, pnl,
//   boughtTimestamp, soldTimestamp, duration
//
// Each row is a complete round-trip trade (already paired by Tradovate), so
// no fill-pairing logic is needed here. We just normalize fields into our
// canonical trade shape.
//
// Direction inference: Performance rows don't include direction explicitly.
// A row is a Long if the buy timestamp came before the sell (bought first,
// sold later). It's a Short if the sell came first (sold first, bought to
// cover). Equal timestamps fall back to Long as a defensive default.
// ─────────────────────────────────────────────────────────────────────────────
function parseTradovatePerformance(headers, dataLines) {
  const idx = (name) => headers.indexOf(name);
  const col = {
    symbol:           idx("symbol"),
    qty:              idx("qty"),
    buyPrice:         idx("buyprice"),
    sellPrice:        idx("sellprice"),
    pnl:              idx("pnl"),
    boughtTimestamp:  idx("boughttimestamp"),
    soldTimestamp:    idx("soldtimestamp"),
    duration:         idx("duration"),
    buyFillId:        idx("buyfillid"),
    sellFillId:       idx("sellfillid"),
  };

  return dataLines
    .filter(l => l.trim())
    .map(line => {
      const cols = line.split(",").map(c => c.trim().replace(/^"|"$/g, ""));
      const get  = (key) => (col[key] !== -1 ? cols[col[key]] : null);

      const bought = parseDateTimeString(get("boughtTimestamp") || "");
      const sold   = parseDateTimeString(get("soldTimestamp")   || "");

      // Long = bought first, sold later. Short = sold first, bought to cover.
      // Compare via ISO timestamps (falls back to date if iso is null).
      const boughtFirst = bought.iso && sold.iso
        ? bought.iso < sold.iso
        : true; // defensive default: treat as Long if we can't tell
      const direction = boughtFirst ? "Long" : "Short";

      const entryRaw = boughtFirst ? bought : sold;
      const exitRaw  = boughtFirst ? sold   : bought;
      const entryPrice = parseFloat(get(boughtFirst ? "buyPrice"  : "sellPrice")) || 0;
      const exitPrice  = parseFloat(get(boughtFirst ? "sellPrice" : "buyPrice"))  || 0;

      const pnl = parsePnl(get("pnl"));

      return {
        symbol:           (get("symbol") || "Unknown").replace(/\s+/g, ""),
        direction,
        entry_time:       entryRaw.time || "",
        exit_time:        exitRaw.time  || "",
        exited_at:        exitRaw.iso   || null,
        trade_date:       entryRaw.date || localTodayYMD(),
        entry_price:      entryPrice,
        exit_price:       exitPrice,
        position_size:    parseFloat(get("qty")) || 1,
        fees:             0,    // Tradovate Performance doesn't export fees
        commissions:      0,    // Same — must be added manually or via Cash History
        pnl,
        result:           pnl > 0 ? "Win" : pnl < 0 ? "Loss" : "Breakeven",
        duration_minutes: parseTradovateDuration(get("duration")),
        // Use the buy fill id as broker_trade_id — but only when it's a real
        // unique integer. Tradovate sometimes exports fill IDs in scientific
        // notation (e.g. "4.96766E+11") which truncates the actual value and
        // makes every trade appear identical, causing false duplicates on
        // re-import. When scientific notation is detected we discard the ID
        // and fall back to fingerprint deduplication instead.
        broker_trade_id:  (() => {
          const raw = get("buyFillId");
          if (!raw) return null;
          const isScientific = /^[\d.]+[eE][+-]?\d+$/.test(String(raw).trim());
          return isScientific ? null : String(raw);
        })(),
        import_source:    "csv",
        created_at:       new Date().toISOString(),
      };
    });
}

// Tradovate Performance duration format: "1min 5sec", "3min 40sec", "2hr 5min".
// Returns minutes (rounded). Returns null if unparseable.
function parseTradovateDuration(raw) {
  if (!raw) return null;
  const s = String(raw).toLowerCase();
  // Capture hours / minutes / seconds in any combination
  const hrMatch  = s.match(/(\d+)\s*hr/);
  const minMatch = s.match(/(\d+)\s*min/);
  const secMatch = s.match(/(\d+)\s*sec/);
  if (!hrMatch && !minMatch && !secMatch) return null;
  const hours = hrMatch  ? Number(hrMatch[1])  : 0;
  const mins  = minMatch ? Number(minMatch[1]) : 0;
  const secs  = secMatch ? Number(secMatch[1]) : 0;
  return Math.round(hours * 60 + mins + secs / 60);
}

// ─────────────────────────────────────────────────────────────────────────────
// SIERRA CHART PARSER
//
// ASSUMPTIONS (unverified — update when real file arrives):
//   - Tab-delimited text file
//   - One row per fill (not per trade)
//   - ActivityType column: filter to "Fill" rows only
//   - Timestamps are UTC
//   - Fields: DateTime, ActivityType, Symbol, TradeAccount,
//             Quantity, Price, FillExecutionServiceID, BuySell
//   - BuySell values: "Buy" or "Sell"
//   - Fills are paired by symbol + account into trades:
//       first Buy/Sell = entry, matching opposite = exit
//
// KNOWN GAPS:
//   - No P&L column — computed from entry/exit price + tick value
//     (tick value not available here, so pnl = 0 until enriched)
//   - Partial fills: grouped by FillExecutionServiceID prefix or time proximity
//   - Commission: not confirmed in export, set to 0
// ─────────────────────────────────────────────────────────────────────────────
function parseSierraChart(text) {
  if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];

  const headers = lines[0]
    .split("\t")
    .map(h => h.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_"));

  const idx = (name) => headers.indexOf(name);
  const col = {
    datetime:    idx("datetime"),
    activitytype: idx("activitytype"),
    symbol:      idx("symbol"),
    account:     idx("tradeaccount"),
    quantity:    idx("quantity"),
    price:       idx("price"),
    fillid:      idx("fillexecutionserviceid"),
    buysell:     idx("buysell"),
  };

  // Extract fill rows only
  const fills = lines.slice(1)
    .filter(l => l.trim())
    .map(line => {
      const cols = line.split("\t").map(c => c.trim().replace(/^"|"$/g, ""));
      const get  = (key) => (col[key] !== -1 ? cols[col[key]] : null);
      const actType = (get("activitytype") || "").toLowerCase();
      if (!actType.includes("fill")) return null;

      const dt = parseDateTimeString(get("datetime") || "");
      const rawSide = (get("buysell") || "").toLowerCase();
      const side = rawSide.startsWith("b") ? "BUY" : rawSide.startsWith("s") ? "SELL" : null;
      if (!side) return null;

      return {
        symbol:   (get("symbol") || "Unknown").replace(/\s+/g, ""),
        side,
        qty:      Math.abs(parseFloat(get("quantity")) || 0),
        price:    parseFloat(get("price")) || 0,
        fillid:   get("fillid") || null,
        iso:      dt.iso,
        date:     dt.date,
        time:     dt.time,
      };
    })
    .filter(Boolean);

  // Pair fills into trades: match BUY + SELL fills by symbol in order
  // ASSUMPTION: fills arrive chronologically; first BUY pairs with first SELL
  // for the same symbol. This handles simple entries but NOT scale-in/scale-out.
  const openFills = {}; // symbol → fill
  const trades = [];

  for (const fill of fills) {
    const key = fill.symbol;
    if (!openFills[key]) {
      openFills[key] = fill;
    } else {
      const entry = openFills[key];
      const exit  = fill;
      delete openFills[key];

      const isBuyFirst = entry.side === "BUY";
      const direction  = isBuyFirst ? "Long" : "Short";
      const entryFill  = isBuyFirst ? entry : exit;
      const exitFill   = isBuyFirst ? exit  : entry;

      trades.push({
        symbol:           entryFill.symbol,
        direction,
        entry_time:       entryFill.time  || "",
        exit_time:        exitFill.time   || "",
        exited_at:        exitFill.iso    || null,
        trade_date:       entryFill.date  || localTodayYMD(),
        entry_price:      entryFill.price,
        exit_price:       exitFill.price,
        position_size:    entryFill.qty,
        fees:             0,
        commissions:      0,
        pnl:              0, // cannot compute without tick value
        result:           "Breakeven", // overridden once pnl is known
        duration_minutes: null,
        broker_trade_id:  entryFill.fillid || null,
        import_source:    "csv",
        created_at:       new Date().toISOString(),
      });
    }
  }

  return trades;
}

// ─────────────────────────────────────────────────────────────────────────────
// NINJATRADER EXECUTIONS PARSER
//
// ASSUMPTIONS (based on TradeZella documented schema — unverified with real file):
//   Headers: Instrument,Action,Quantity,Price,Time,ID,E/X,Position,
//            Order ID,Name,Commission,Rate,Account,Connection
//
//   - One row per fill
//   - E/X column: "Entry" or "Exit" (may also appear as "E" / "X")
//   - Action column: "Buy" or "Sell"
//   - Timestamps: local time (timezone unknown — stored as-is)
//   - Fills paired by: E/X flag — Entry fill followed by Exit fill
//     for the same Instrument
//   - No P&L column — set to 0 (same gap as Sierra Chart)
//   - Commission per fill — summed across entry+exit
//
// KNOWN GAPS:
//   - Columns are user-configurable; any column may be absent
//   - Scale-in/scale-out: multiple Entry fills before an Exit
//     → current logic pairs first Entry with first Exit only
//   - Timezone not in export — times stored without offset
// ─────────────────────────────────────────────────────────────────────────────
function parseNinjaTraderExecutions(headers, dataLines) {
  const idx  = (name) => headers.indexOf(name);
  const col  = {
    instrument:  idx("instrument"),
    action:      idx("action"),
    quantity:    idx("quantity"),
    price:       idx("price"),
    time:        idx("time"),
    id:          idx("id"),
    ex:          idx("e_x"),
    order_id:    idx("order_id"),
    commission:  idx("commission"),
  };

  const fills = dataLines
    .filter(l => l.trim())
    .map(line => {
      const cols = line.split(",").map(c => c.trim().replace(/^"|"$/g, ""));
      const get  = (key) => (col[key] !== -1 ? cols[col[key]] : null);

      const rawEx     = (get("ex") || "").toLowerCase().trim();
      // E/X may be "Entry"/"Exit" or "E"/"X"
      const isEntry   = rawEx === "entry" || rawEx === "e";
      const isExit    = rawEx === "exit"  || rawEx === "x";
      if (!isEntry && !isExit) return null;

      const rawAction = (get("action") || "").toLowerCase();
      const side      = rawAction.startsWith("b") ? "BUY" : rawAction.startsWith("s") ? "SELL" : null;
      if (!side) return null;

      const dt = parseDateTimeString(get("time") || "");

      return {
        symbol:     (get("instrument") || "Unknown").replace(/\s+/g, ""),
        side,
        isEntry,
        qty:        Math.abs(parseFloat(get("quantity")) || 0),
        price:      parseFloat(get("price")) || 0,
        commission: parseFloat(get("commission")) || 0,
        fillid:     get("id") || get("order_id") || null,
        iso:        dt.iso,
        date:       dt.date,
        time:       dt.time,
      };
    })
    .filter(Boolean);

  // Pair Entry fills with Exit fills by symbol, in order
  const openEntries = {}; // symbol → fill
  const trades = [];

  for (const fill of fills) {
    const key = fill.symbol;
    if (fill.isEntry) {
      openEntries[key] = fill; // last entry wins for simple in/out
    } else {
      const entry = openEntries[key];
      if (!entry) continue; // exit with no matching entry — skip
      delete openEntries[key];

      const direction = entry.side === "BUY" ? "Long" : "Short";
      const totalComm = entry.commission + fill.commission;

      trades.push({
        symbol:           entry.symbol,
        direction,
        entry_time:       entry.time  || "",
        exit_time:        fill.time   || "",
        exited_at:        fill.iso    || null,
        trade_date:       entry.date  || localTodayYMD(),
        entry_price:      entry.price,
        exit_price:       fill.price,
        position_size:    entry.qty,
        fees:             0,
        commissions:      totalComm,
        pnl:              0, // no P&L in NT executions export
        result:           "Breakeven",
        duration_minutes: null,
        broker_trade_id:  entry.fillid || null,
        import_source:    "csv",
        created_at:       new Date().toISOString(),
      });
    }
  }

  return trades;
}

// ─────────────────────────────────────────────────────────────────────────────
// TRADINGVIEW PAPER TRADING PARSER
//
// Header signature (after normalization):
//   symbol, side, type, quantity, limit_price, stop_price, fill_price,
//   commission, placing_time, closing_time, order_id, level_id, leverage, margin
//
// Key facts:
//   - One row per fill (not per trade)
//   - Entry fills: Leverage column is populated (e.g. "25:01:00")
//   - Exit fills:  Leverage column is empty/NaN
//   - Symbol format: "CME_MINI:MNQ1!" → normalize to "MNQ"
//   - Timestamps: "6/11/2026 13:44" (local time, no timezone)
//   - No P&L column — computed from entry/exit price * point value * qty
//   - Direction: entry Side = "Buy" → Long, "Sell" → Short
//
// Point values (per contract, per point):
//   MNQ=$2, MES=$5, NQ=$20, ES=$50, CL=$10, GC=$10, RTY=$10, YM=$5
//
// Pairing logic:
//   For each exit fill, find the most recent entry fill for the same symbol
//   that occurred before the exit closing_time.
// ─────────────────────────────────────────────────────────────────────────────

const TV_POINT_VALUES = {
  MNQ: 2, MES: 5, NQ: 20, ES: 50,
  CL: 10, GC: 10, RTY: 10, YM: 5,
  MCL: 1, MGC: 1,
};

function normalizeTVSymbol(raw) {
  if (!raw) return "Unknown";
  // CME_MINI:MNQ1! → MNQ
  let s = raw.includes(":") ? raw.split(":")[1] : raw;
  // Strip trailing digits and !
  s = s.replace(/[0-9!]+$/, "");
  return s || raw;
}

function parseTVDate(raw) {
  if (!raw) return { date: null, time: null, iso: null };
  const trimmed = String(raw).trim();
  // Format: "6/11/2026 13:44"
  const m = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})$/);
  if (!m) return { date: null, time: null, iso: null };
  const [, mo, d, y, hh, mm] = m;
  const date = `${y}-${mo.padStart(2,"0")}-${d.padStart(2,"0")}`;
  const time = `${hh.padStart(2,"0")}:${mm}:00`;
  let iso = null;
  try { iso = new Date(`${date}T${time}`).toISOString(); } catch {}
  return { date, time, iso };
}

function parseTradingView(headers, dataLines) {
  const idx = (name) => headers.indexOf(name);
  const col = {
    symbol:       idx("symbol"),
    side:         idx("side"),
    type:         idx("type"),
    quantity:     idx("quantity"),
    stop_price:   idx("stop_price"),
    fill_price:   idx("fill_price"),
    commission:   idx("commission"),
    placing_time: idx("placing_time"),
    closing_time: idx("closing_time"),
    order_id:     idx("order_id"),
  };

  const fills = dataLines
    .filter(l => l.trim())
    .map(line => {
      const cols = line.split(",").map(c => c.trim().replace(/^"|"$/g, ""));
      const get  = (key) => (col[key] !== -1 ? cols[col[key]] : null);

      const closing = parseTVDate(get("closing_time"));
      const placing = parseTVDate(get("placing_time"));
      // fill_price may be empty for Stop orders — fall back to stop_price
      const price   = parseFloat(get("fill_price")) || parseFloat(get("stop_price")) || 0;

      return {
        symbol:      normalizeTVSymbol(get("symbol")),
        side:        (get("side") || "").trim(),
        qty:         Math.abs(parseFloat(get("quantity")) || 0),
        price,
        commission:  parseFloat(get("commission")) || 0,
        closingIso:  closing.iso,
        closingDate: closing.date,
        closingTime: closing.time,
        placingIso:  placing.iso,
        orderId:     get("order_id") || null,
      };
    })
    .filter(f => f.qty > 0 && f.price > 0);

  // Sort by closing time (fill time), then placing time as tiebreaker.
  // When two fills close at the same minute (e.g. a stop-loss and a new
  // market order both filling at 07:13), the one placed earlier should
  // sort first — it belongs to the prior trade, not the next one.
  fills.sort((a, b) => {
    const ca = a.closingIso || "", cb = b.closingIso || "";
    if (ca !== cb) return ca < cb ? -1 : 1;
    const pa = a.placingIso || "", pb = b.placingIso || "";
    return pa < pb ? -1 : pa > pb ? 1 : 0;
  });

  // Group fills by symbol, accumulate net position, emit trade when net = 0.
  // Handles staggered entries, staggered exits, and TradingView bracket orders.
  const bySymbol = {};
  for (const fill of fills) {
    if (!bySymbol[fill.symbol]) bySymbol[fill.symbol] = [];
    bySymbol[fill.symbol].push(fill);
  }

  const trades = [];

  for (const [sym, symFills] of Object.entries(bySymbol)) {
    let buys = [], sells = [];

    const emitTrade = () => {
      if (!buys.length || !sells.length) { buys = []; sells = []; return; }
      const totalBuyQty  = buys.reduce((s, f) => s + f.qty, 0);
      const totalSellQty = sells.reduce((s, f) => s + f.qty, 0);
      const tradeQty     = Math.min(totalBuyQty, totalSellQty);
      if (!tradeQty) { buys = []; sells = []; return; }

      const firstBuy  = buys[0].closingIso  || "";
      const firstSell = sells[0].closingIso || "";
      const isLong    = firstBuy <= firstSell;

      const entryFills = isLong ? buys  : sells;
      const exitFills  = isLong ? sells : buys;

      const totalEntryQty = entryFills.reduce((s, f) => s + f.qty, 0);
      const avgEntry = entryFills.reduce((s, f) => s + f.price * f.qty, 0) / totalEntryQty;
      const totalExitQty = exitFills.reduce((s, f) => s + f.qty, 0);
      const avgExit  = exitFills.reduce((s, f) => s + f.price * f.qty, 0) / totalExitQty;

      const pv  = TV_POINT_VALUES[sym] || 1;
      const pnl = Math.round((isLong ? avgExit - avgEntry : avgEntry - avgExit) * pv * tradeQty * 100) / 100;
      const totalComm = [...buys, ...sells].reduce((s, f) => s + (f.commission || 0), 0);

      const entryByTime = [...entryFills].sort((a, b) => (a.closingIso || "") < (b.closingIso || "") ? -1 : 1);
      const exitByTime  = [...exitFills].sort((a, b)  => (a.closingIso || "") < (b.closingIso || "") ? -1 : 1);
      const firstEntryFill = entryByTime[0];
      const lastExitFill   = exitByTime[exitByTime.length - 1];

      trades.push({
        symbol:           sym,
        direction:        isLong ? "Long" : "Short",
        entry_time:       firstEntryFill.closingTime || "",
        exit_time:        lastExitFill.closingTime   || "",
        exited_at:        lastExitFill.closingIso    || null,
        trade_date:       firstEntryFill.closingDate || localTodayYMD(),
        entry_price:      parseFloat(avgEntry.toFixed(4)),
        exit_price:       parseFloat(avgExit.toFixed(4)),
        position_size:    tradeQty,
        fees:             0,
        commissions:      parseFloat(totalComm.toFixed(2)),
        pnl,
        result:           pnl > 0 ? "Win" : pnl < 0 ? "Loss" : "Breakeven",
        duration_minutes: null,
        broker_trade_id:  entryFills[0].orderId || null,
        import_source:    "csv",
        created_at:       new Date().toISOString(),
      });

      buys = []; sells = [];
    };

    let netQty = 0;
    for (const fill of symFills) {
      if (fill.side === "Buy") { buys.push(fill); netQty += fill.qty; }
      else { sells.push(fill); netQty -= fill.qty; }
      if (netQty === 0) emitTrade();
    }
    if (buys.length && sells.length) emitTrade();
  }

  return trades;
}
